/**
 * Unified Haptic Feedback
 *
 * Uses Capacitor Haptics on native Android (navigator.vibrate is unreliable
 * inside Capacitor WebView) with a web Vibration API fallback.
 */

let HapticsPlugin: any = null;
let initPromise: Promise<void> | null = null;

function initHaptics(): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    try {
      const pkg = '@capacitor/haptics';
      const mod = await import(/* @vite-ignore */ pkg);
      HapticsPlugin = mod.Haptics;
    } catch {
      // Not available (web build) — fall back to navigator.vibrate
    }
  })();
  return initPromise;
}

// Kick off init immediately
initHaptics();

type ImpactStyle = 'light' | 'medium' | 'heavy';

function impactOnce(style: ImpactStyle = 'medium'): void {
  if (HapticsPlugin) {
    const s = style === 'heavy' ? 'Heavy' : style === 'light' ? 'Light' : 'Medium';
    HapticsPlugin.impact({ style: s }).catch(() => {});
    return;
  }
  const ms = style === 'heavy' ? 30 : style === 'light' ? 5 : 15;
  try { navigator.vibrate?.(ms); } catch {}
}

function vibratePattern(pattern: number[]): void {
  if (HapticsPlugin) {
    // Capacitor Haptics doesn't support patterns — simulate with sequential impacts
    let delay = 0;
    for (let i = 0; i < pattern.length; i++) {
      if (i % 2 === 0 && pattern[i] > 0) {
        setTimeout(() => impactOnce('light'), delay);
      }
      delay += pattern[i];
    }
    return;
  }
  try { navigator.vibrate?.(pattern); } catch {}
}

/**
 * Drop-in replacement for navigator.vibrate that works on native Android.
 * Accepts a single duration (ms) or a pattern array.
 */
export function vibrate(patternOrMs: number | number[] = 15, style: ImpactStyle = 'medium'): void {
  if (Array.isArray(patternOrMs)) {
    vibratePattern(patternOrMs);
  } else {
    impactOnce(style);
  }
}
